export interface QuestionOption {
  id: string;
  text: string;
  keywords: string[];
}

export interface PromptMapItem {
  starters: string[];
  example: string;
  encouragement: string;
}

export interface Substep {
  id: string;
  text: string;
  type: 'single' | 'text';
  options?: QuestionOption[];
  placeholder?: string;
  promptMap?: Record<string, PromptMapItem>;
}

export interface Question {
  id: string;
  text: string;
  type: 'single' | 'multiple' | 'text' | 'ranking' | 'two_step';
  options?: QuestionOption[];
  placeholder?: string;
  substeps?: Substep[];
  encouragementText?: string;
  allowSkip?: boolean;
  skipText?: string;
}

export const DIAGNOSTIC_QUESTIONS: Question[] = [
  {
    id: 'q1_core_capability',
    text: "What's something you've done repeatedly that others find difficult?",
    type: 'single',
    options: [
      {
        id: 'organized',
        text: 'Organized or managed things/people',
        keywords: ['operations', 'project_management', 'logistics', 'coordination']
      },
      {
        id: 'fixed',
        text: 'Fixed or troubleshot technical problems',
        keywords: ['technical_support', 'IT', 'maintenance', 'troubleshooting']
      },
      {
        id: 'created',
        text: 'Created or built things (physical or digital)',
        keywords: ['design', 'development', 'trades', 'construction']
      },
      {
        id: 'taught',
        text: 'Taught or explained complex concepts',
        keywords: ['training', 'customer_success', 'education', 'mentoring']
      },
      {
        id: 'handled_money',
        text: 'Handled money, budgets, or tracked details',
        keywords: ['accounting', 'finance', 'admin', 'bookkeeping']
      },
      {
        id: 'difficult_people',
        text: 'Dealt with difficult people or high-stress situations',
        keywords: ['customer_service', 'HR', 'healthcare', 'crisis_management']
      },
      {
        id: 'analyzed',
        text: 'Analyzed data or improved processes',
        keywords: ['data_analysis', 'business_intelligence', 'qa', 'optimization']
      },
      {
        id: 'care',
        text: 'Provided care or support to others',
        keywords: ['healthcare', 'social_services', 'customer_success', 'caregiving']
      }
    ]
  },
  {
    id: 'q2_problem_solving',
    text: 'Tell us about a time you solved a problem',
    type: 'two_step',
    substeps: [
      {
        id: 'q2a_problem_type',
        text: 'What kind of problem have you solved recently? (Pick the one that sounds most like you)',
        type: 'single',
        options: [
          { id: 'fixed', text: 'Something broke and I fixed it', keywords: ['technical', 'troubleshooting'] },
          { id: 'organized', text: 'Things were disorganized and I made a system', keywords: ['organization', 'process'] },
          { id: 'calmed', text: 'Someone was upset and I helped them', keywords: ['people', 'support'] },
          { id: 'customer', text: 'A customer had a problem and I solved it', keywords: ['customer_service', 'problem_solving'] },
          { id: 'schedule', text: 'We were behind schedule and I got us back on track', keywords: ['coordination', 'urgency'] },
          { id: 'improved', text: 'I found a better way to do something', keywords: ['innovation', 'improvement'] },
          { id: 'figured_out', text: "Something wasn't adding up and I figured it out", keywords: ['analysis', 'investigation'] }
        ]
      },
      {
        id: 'q2b_story',
        text: "What happened? (Just tell it like you're telling a friend)",
        type: 'text',
        placeholder: 'Start typing your story here...',
        promptMap: {
          'fixed': {
            starters: [
              'At work, [thing] stopped working, so I...',
              'One day I noticed [problem], and I...',
              'We had an issue with [thing], I fixed it by...'
            ],
            example: 'At the restaurant, the ice machine stopped making ice during dinner rush. I checked the back and saw someone had unplugged it to charge their phone. Plugged it back in and we were fine. Now there\'s a sign that says "DO NOT UNPLUG."',
            encouragement: 'Great! Just describe what broke, what you did, and what happened. No fancy language needed.'
          },
          'organized': {
            starters: [
              'At [place], things were messy because...',
              'I noticed that [problem], so I...',
              'We were wasting time on [thing], so I made a system where...'
            ],
            example: 'At the warehouse, nobody could find anything because boxes were stacked randomly. I spent a weekend labeling zones and making a map. Now everyone can find stuff in under 2 minutes instead of 20.',
            encouragement: 'Perfect! Tell us what was messy, how you organized it, and what improved.'
          },
          'calmed': {
            starters: [
              'Someone was upset about [situation], so I...',
              'A [person] was frustrated because [problem], and I helped by...',
              'There was tension over [issue], and I...'
            ],
            example: 'A coworker was really stressed about covering two shifts in a row. I talked to the manager and volunteered to take the second shift. She was so grateful and it made the whole team work better that week.',
            encouragement: 'Great! Tell us who was upset, why, and how you helped them feel better.'
          },
          'customer': {
            starters: [
              'A customer came in and...',
              'Someone called because...',
              'I helped a customer who...'
            ],
            example: 'A customer came in wanting to return jeans but had no receipt. I looked up their credit card and found the purchase. Did the return and she thanked me for not making it difficult. She comes back every month now.',
            encouragement: 'Perfect! Tell us who the customer was, what they needed, and how you helped them.'
          },
          'schedule': {
            starters: [
              'We were behind on [project/task] because...',
              'The team was falling behind, so I...',
              'We had a deadline for [thing], and I got us back on track by...'
            ],
            example: 'We were behind on inventory before Black Friday. I came in early for three days, reorganized the system, and got two friends to help for a few hours. We finished with a day to spare and everything went smooth.',
            encouragement: 'Excellent! Tell us what you were behind on, what you did, and whether it worked.'
          },
          'improved': {
            starters: [
              'I noticed we were doing [thing] the hard way, so I...',
              'There was a better way to [task], so I tried...',
              'I had an idea to make [process] easier by...'
            ],
            example: 'We were manually counting inventory every week with pen and paper. I suggested using a simple spreadsheet on a tablet. Cut the time from 4 hours to 45 minutes and no more math errors.',
            encouragement: 'Nice! Tell us what the old way was, what you changed, and how it helped.'
          },
          'figured_out': {
            starters: [
              'The numbers weren\'t adding up because...',
              'Something was wrong with [thing], and I discovered...',
              'I investigated [problem] and found out that...'
            ],
            example: 'The cash register was always short by small amounts. I watched for a week and realized the system wasn\'t calculating tax right on discounted items. Told the manager and they fixed it in the system. No more shortages.',
            encouragement: 'Great! Tell us what wasn\'t adding up, how you figured it out, and what you found.'
          }
        }
      }
    ],
    encouragementText: "💡 Don't overthink it - even small wins count! We want to see how you think, not how well you write.",
    allowSkip: true,
    skipText: "I can't think of an example right now"
  },
  {
    id: 'q3_work_style',
    text: 'Which describes how you prefer to work?',
    type: 'single',
    options: [
      {
        id: 'structured',
        text: 'Structured tasks with clear steps to follow',
        keywords: ['operations', 'admin', 'qa', 'compliance']
      },
      {
        id: 'open_ended',
        text: 'Open-ended problems I figure out myself',
        keywords: ['project_management', 'analysis', 'consulting']
      },
      {
        id: 'hands',
        text: 'Working with my hands on physical tasks',
        keywords: ['trades', 'warehouse', 'logistics', 'manufacturing']
      },
      {
        id: 'people',
        text: 'Working directly with people',
        keywords: ['customer_service', 'sales', 'healthcare', 'hospitality']
      },
      {
        id: 'data_systems',
        text: 'Working with data, systems, or technology',
        keywords: ['tech', 'analysis', 'finance', 'IT']
      },
      {
        id: 'mix',
        text: 'Mix of different types of tasks throughout the day',
        keywords: ['coordinator', 'hospitality_management', 'admin']
      }
    ]
  },
  {
    id: 'q4_natural_strengths',
    text: 'What do coworkers, friends, or family ask YOU for help with? (Choose up to 3)',
    type: 'multiple',
    options: [
      {
        id: 'technical',
        text: 'Technical stuff (computers, tools, fixing things)',
        keywords: ['IT', 'technical_support', 'maintenance']
      },
      {
        id: 'planning',
        text: 'Planning, organizing, or coordinating',
        keywords: ['operations', 'project_management', 'logistics']
      },
      {
        id: 'creative',
        text: 'Creative projects or design',
        keywords: ['design', 'marketing', 'content']
      },
      {
        id: 'emotional',
        text: 'Emotional support or giving advice',
        keywords: ['customer_service', 'healthcare', 'HR']
      },
      {
        id: 'figuring_out',
        text: 'Figuring out how things work',
        keywords: ['analysis', 'troubleshooting', 'research']
      },
      {
        id: 'execution',
        text: 'Getting things done quickly',
        keywords: ['operations', 'logistics', 'sales']
      },
      {
        id: 'explaining',
        text: 'Explaining complicated things clearly',
        keywords: ['training', 'customer_success', 'support']
      },
      {
        id: 'conflict',
        text: 'Handling conflict or difficult conversations',
        keywords: ['HR', 'customer_service', 'management']
      },
      {
        id: 'ideas',
        text: 'Coming up with new ideas or solutions',
        keywords: ['product', 'consulting', 'strategy']
      },
      {
        id: 'follow_through',
        text: 'Following through on commitments',
        keywords: ['operations', 'admin', 'project_management']
      }
    ]
  },
  {
    id: 'q5_current_situation',
    text: "What's your current work situation?",
    type: 'single',
    options: [
      {
        id: 'full_time',
        text: 'Working full-time (want to transition to something better)',
        keywords: []
      },
      {
        id: 'part_time',
        text: 'Working part-time (need more income/hours)',
        keywords: []
      },
      {
        id: 'unemployed',
        text: 'Unemployed and actively looking',
        keywords: []
      },
      {
        id: 'caregiver',
        text: 'Caregiver (need flexible work options)',
        keywords: ['remote', 'flexible']
      },
      {
        id: 'training',
        text: 'In training/school (planning my next step)',
        keywords: []
      },
      {
        id: 'military',
        text: 'Recently separated from military',
        keywords: []
      },
      {
        id: 'returning',
        text: 'Returning to work after time away',
        keywords: []
      }
    ]
  },
  {
    id: 'q6_income_needs',
    text: "What's your minimum acceptable salary?",
    type: 'single',
    options: [
      {
        id: '30_40k',
        text: '$30-40K/year (getting started, building experience)',
        keywords: []
      },
      {
        id: '40_55k',
        text: '$40-55K/year (supporting myself comfortably)',
        keywords: []
      },
      {
        id: '55_70k',
        text: '$55-70K/year (supporting a family)',
        keywords: []
      },
      {
        id: '70k_plus',
        text: '$70K+ per year (high earner path)',
        keywords: []
      },
      {
        id: 'not_sure',
        text: "I'm not sure / it depends on the area",
        keywords: []
      }
    ]
  },
  {
    id: 'q7_location',
    text: 'Where would you like to work?',
    type: 'two_step',
    substeps: [
      {
        id: 'q7a_preferred_location',
        text: 'What type of area would you prefer to work in?',
        type: 'single',
        options: [
          {
            id: 'major_metro',
            text: 'Major metro area',
            keywords: ['major_metro', 'urban']
          },
          {
            id: 'midsize',
            text: 'Mid-size city or suburbs',
            keywords: ['midsize', 'suburban']
          },
          {
            id: 'small_town',
            text: 'Small town or rural area',
            keywords: ['small_town', 'rural']
          },
          {
            id: 'doesnt_matter',
            text: "Doesn't matter - I'm flexible on location",
            keywords: ['flexible', 'any']
          }
        ]
      },
      {
        id: 'q7b_work_arrangement',
        text: 'What work arrangement fits your life?',
        type: 'single',
        options: [
          {
            id: 'onsite_local',
            text: 'On-site, close to where I live now',
            keywords: ['onsite', 'local']
          },
          {
            id: 'onsite_commute',
            text: 'On-site, willing to commute further for the right job',
            keywords: ['onsite', 'commute']
          },
          {
            id: 'hybrid',
            text: 'Hybrid (some remote, some in-office)',
            keywords: ['hybrid', 'flexible']
          },
          {
            id: 'remote',
            text: 'Fully remote (work from home)',
            keywords: ['remote', 'remote_only']
          },
          {
            id: 'relocate',
            text: 'Open to relocating for the right opportunity',
            keywords: ['relocate', 'flexible']
          }
        ]
      }
    ],
    encouragementText: "💡 This helps us match you with jobs that fit your lifestyle. Your preferred work location affects salary ranges and job availability."
  }
];
